import React from 'react';
import { useSelector } from 'react-redux';

/*
 * SIZE NOTE — this block is `body2` (14px), NOT `body1` (16px).
 *
 * It was body1, which was 14px until the type-scale rework set body1 to the
 * body step at 16. That silently promoted the whole activity block to the SAME
 * size as the product title above it, and since the block runs to three lines it
 * ended up carrying more ink than the name of the thing it describes. Reported
 * as "the activity text is bigger than the title" — it was equal, which at three
 * times the line count reads as bigger.
 *
 * Activity is supporting information about a certificate; the certificate's name
 * is the subject. 14 vs 16 is the whole distinction, and the event NAME keeps
 * weight 500 so the demotion does not flatten the block into one grey paragraph.
 */

import { Icon, EntityAvatar } from 'components/UI';
import { CountdownTimer } from 'components/UI/CountdownTimer';
import { ShieldAlert } from 'lucide-react';

import { getName } from 'utils/parsers';
import { resolvePersonName } from '@repo/types';

import { formatActivityTime } from 'utils/date';
import {
  ANONYMOUS_SENDER_LABEL,
  isAnonymousGift,
  isIncomingTransferFor,
} from 'utils/certificateUtils';
import { useTransferCertificate } from 'hooks/api/useTransferCertificate';
import { Certificate, CertificateStatus } from 'types';
import { TransferType } from '@repo/types';

import { Typography, useTheme, Box, Stack, alpha } from '@mui/material';
import { getScanLocationLabel, isInvalidScan } from '@repo/ui';

/*
 * The activity row's text indent, derived rather than typed as a magic 60.
 *
 * Row 1 is [glyph slot][gap][headline ... time], so the headline's left edge sits at
 * `slot + gap` from the row's own left edge. Row 2 (the description) aligns to that
 * SAME edge, so it has to be the same sum — and the only way to guarantee it stays the
 * same sum is to compute it from the two values that produce it. Hard-coding 60 here
 * would be correct today and silently wrong the first time the glyph slot or the gap
 * moves, which is exactly the kind of drift the row has already been through once.
 *
 * The slot is 32px because the markup asks Tailwind for `w-8`/`min-w-8`; the two must
 * agree, so if that class changes this constant has to change with it. (There is no way
 * to read a Tailwind class back out at runtime, so this is a mirror, not a derivation.)
 *
 * The gap is expressed in PX, not as a `gap: 1.5` multiplier, and that matters:
 * `apps/app` runs on MUI's DEFAULT 8px base rather than `@repo/ui`'s 4px
 * (working-conventions §29), so the same factor renders at double the size across the
 * two apps. Stating the pixel value removes the question.
 */
const ACTIVITY_GLYPH_SLOT_PX = 32;
const ACTIVITY_ROW_GAP_PX = 12;
const ACTIVITY_TEXT_INDENT_PX = ACTIVITY_GLYPH_SLOT_PX + ACTIVITY_ROW_GAP_PX;

/**
 * The fill inside the slot — the avatar's diameter, the glyph's box.
 *
 * Smaller than the slot by design, so a tinted well (the invalid-scan case) has
 * somewhere to be a well rather than a tight ring. The ratio is the one the row
 * already had at 40-in-48; it is restated as a derivation so the two cannot
 * drift apart the next time the slot moves.
 */
const ACTIVITY_GLYPH_FILL_PX = ACTIVITY_GLYPH_SLOT_PX - 4;

/**
 * The activity strip's resting height, and why it is a number worth exporting.
 *
 * The strip was 80px — a 48px slot in 16px of vertical padding — against a
 * title/SKU block measuring 62. So the SUPPORTING fact about a certificate was
 * taller than the certificate's own name and identifier, and on a grid card the
 * difference was the first thing you saw. Reported exactly that way.
 *
 * 32 + 12 + 12 = 56 puts it under the block it sits beneath, which is the whole
 * requirement: activity may equal the title block, never exceed it.
 *
 * This is a FLOOR, not a cap. The two- and three-line rows (a transfer notice
 * with its description) are legitimately taller, and clamping them would just
 * move the problem into an ellipsis. What is fixed is the one-line case, which
 * is the common one and the one that was over.
 *
 * Exported because `CertificatesTeaser` sets the padding that produces it, so
 * the card and this file have to agree on the arithmetic or the floor stops
 * being the height.
 */
export const ACTIVITY_STRIP_PAD_Y_PX = 12;
export const ACTIVITY_STRIP_MIN_H =
  ACTIVITY_GLYPH_SLOT_PX + ACTIVITY_STRIP_PAD_Y_PX * 2;

type StatusNotificationProps = {
  refetchCertificates: () => void;
} & Certificate;

/**
 * Statuses whose activity copy is entirely ABOUT a scan ("Last scanned <date>
 * <place>"). For these there is nothing to say until a scan actually exists.
 * Every other status (the transfer/gift/cancel family) carries its own sentence
 * and is meaningful the moment the certificate enters that state.
 */
// `stolen` is deliberately NOT here: it has something to say either way — the last
// sighting if the tag has been scanned, and the "report it" prompt if it hasn't.
const SCAN_BASED_STATUSES = ['active', 'inactive', 'lost'];

/**
 * Does the activity row have anything real to say?
 *
 * Without this the card rendered the literal words "Last scanned" for a
 * certificate with `scan_counter: 0` and no `last_scan` at all — asserting an
 * event that never happened, under a heading, with an icon. On the seeded list
 * that was 5 of 9 cards. An empty state that states a falsehood is worse than no
 * empty state, so the whole block (divider included) collapses instead.
 *
 * Exported so `CertificatesTeaser` decides on the SAME condition that this
 * component renders on — otherwise the card draws a divider above a section that
 * returns null, which is exactly the stray-hairline bug this replaced.
 */
export const hasActivityToShow = (certificate: {
  status?: string | null;
  last_scan?: unknown;
  created_at?: string | null;
}): boolean => {
  const { status, last_scan, created_at } = certificate;
  if (!status) return false;
  /*
    VRG-720: a never-scanned certificate is no longer EMPTY, it falls back to the
    event every certificate has — see `issuedBlock`. So the scan-based statuses
    have something to say whenever either fact exists, and the only remaining
    false is a payload carrying neither, where there is genuinely nothing true to
    print.
  */
  if (SCAN_BASED_STATUSES.includes(status)) return Boolean(last_scan) || Boolean(created_at);
  return true;
};

export const StatusNotification = ({
  status,
  id,
  transfer,
  company,
  last_scan,
  created_at,
  refetchCertificates,
}: StatusNotificationProps) => {
  const { palette } = useTheme();
  const fromCustomer = transfer?.fromCustomer;
  const toCustomer = transfer?.toCustomer;

  // VERA-304: WHICH SIDE OF THE TRANSFER IS LOOKING?
  //
  // This map is keyed on CERTIFICATE status, and a certificate has exactly one status
  // for both parties — so `transfer_initiated` rendered the sender's "Transfer out /
  // Transferred to <them>" for everyone. That was invisible while a recipient's list
  // could not contain a pending transfer at all; the moment the list query started
  // returning incoming ones, every recipient would have been told they had transferred
  // the item TO the person who was actually sending it to them.
  //
  // Same identity test the detail-page card uses (`useTransferCardState`): the profile
  // carries both an owner id and a user id and the serializer can return either, so
  // both are compared.
  const { profile } = useSelector(({ auth }: any) => auth);
  // `isIncomingTransferFor` (utils/certificateUtils), not a fourth inline copy of the
  // same two-id comparison — `PendingTransferNotification` and `useTransferCardState`
  // already ask this question and all three must answer it identically.
  const isIncoming = isIncomingTransferFor(toCustomer, profile);
  const expire_at = transfer?.expire_at;

  // VERA-304: a BRAND handing an item to its first owner (`primary`) and a customer
  // gifting one to another customer (`gift`) are different events, and this component
  // called both a "transfer" because it branched on certificate STATUS alone — a
  // status a gift and a brand handover share. Calling a brand's delivery a gift (or a
  // friend's gift a transfer) misdescribes who the item came from and why.
  //
  // `transfer.type` is on the payload: the aggregate SQL selects `"t"."type" as
  // "transfer_type"` and `toCertificateFullInfo` spreads the transfer row through
  // untouched, which is the same field `useTransferCardState` already branches on for
  // the detail page's card. `sell` is a third member of the enum with no flow behind
  // it yet, so anything that is not explicitly a gift reads as a transfer.
  const isGift = transfer?.type === TransferType.Gift;

  const { cancelTransfer } = useTransferCertificate();

  const cancelTransferHandler = () => {
    cancelTransfer({ certificateId: id });
    refetchCertificates();
  };

  /**
   * VRG-486 — an anonymous gift is a THIRD case, and it has to be tested before the
   * company one or it silently becomes the company one.
   *
   * This row is where the anonymity leak was most visible: it rendered "A gift from
   * spencer@usevergence.com" — the sender's literal email address, for a gift sent
   * with "Hide your name from the recipient" on. The API now withholds
   * `fromCustomer` from every reader but the sender, so the address is gone from the
   * payload entirely rather than merely unrendered.
   *
   * But that redaction turns `fromCustomer` null, and the two lines below read a null
   * `fromCustomer` as "the sender is the BRAND" (which is true for a `primary`
   * transfer, where there genuinely is no person). Left alone they would have
   * relabelled the anonymous gift "A gift from <Brand>", with the brand's logo — a
   * confident and wrong attribution, and a worse failure than the leak, because a
   * reader has no way to tell it is wrong. Hence the explicit check on
   * `metadata.isAnonymous` rather than an inference from absence.
   */
  const anonymousGift = isAnonymousGift(transfer as never);

  // The seller is a PERSON on a peer-to-peer transfer and the BRAND otherwise — the
  // same `fromCustomer` test that picks the image picks the shape it is drawn in.
  const sellerIsCompany = !fromCustomer && !anonymousGift;
  const image = anonymousGift ? undefined : fromCustomer ? fromCustomer.avatar : company?.logo;
  /*
   * VERA-304 — name, else `@handle`, NEVER the email address, and never 'Unknown'.
   *
   * Both lines below used to end `|| x.email`, so a counterparty with no first/last
   * name was announced on the LIST by their email address. That is the same defect
   * the anonymous-gift redaction was built to close, one row over and for a sender
   * who merely lacks a name rather than one who asked to be hidden — and it
   * contradicts `UserPreview`'s own stated rule, "never render the email for a person
   * who HAS an account".
   *
   * `resolvePersonName` (`@repo/types`) is the shared resolver that stops at the
   * handle and returns null rather than reaching for an address. `getName` stays as
   * the first choice because it applies this app's own formatting.
   *
   * 'Unknown' is gone for the reason it was removed from the customer aggregate
   * earlier in this epic: it is TRUTHY, so it silently defeated three correct
   * downstream `if (name)` guards while looking like a harmless placeholder.
   *
   * Both names land INSIDE sentences ("Gift sent to <b>{buyer}.</b> Not claimed yet"),
   * so returning null here would render a stray full stop. Each side therefore falls
   * back to the generic noun for its role — the same "the sender" wording
   * `GiftInActionsSection` already uses for an unidentifiable party, so the flow reads
   * consistently rather than inventing a second phrasing.
   */
  const personName = (p: Parameters<typeof getName>[0]) =>
    getName(p) || resolvePersonName(p as never);
  const seller = anonymousGift
    ? ANONYMOUS_SENDER_LABEL
    : fromCustomer
      ? personName(fromCustomer) || 'the sender'
      : company?.name;
  const buyer = toCustomer ? personName(toCustomer) || 'the recipient' : 'the recipient';

  // VERA-304: the transfer rows drew a generic `UserRoundMinus` glyph, so the one
  // question the row raises — WHO — was answered only by the sentence beside it, and
  // every transfer row in a list looked identical at a glance.
  //
  // The counterparty is whichever end the viewer is NOT: the sender when the item is
  // arriving, the recipient when it is leaving. Same objects the sentence names, so
  // the face and the name can never disagree.
  //
  // `entity` is chosen from what the party IS, never from the row it is on
  // (EntityAvatar's own rule): an incoming `primary` has no `fromCustomer` at all —
  // the sender is the BRAND — and a brand mark must not be circle-cropped. An
  // outgoing transfer is always to a person.
  //
  // `name` feeds the initials fallback, and is passed as null rather than 'Unknown'
  // when there is no recipient record: the shared avatar then draws its profile
  // glyph instead of initialling a placeholder word as "UN".
  const counterparty = isIncoming
    ? { entity: sellerIsCompany ? ('company' as const) : ('person' as const), src: image, name: seller }
    : { entity: 'person' as const, src: toCustomer?.avatar, name: toCustomer ? buyer : null };
  // VERA-476: the shared resolver, not `${location.latitude}, ${location.longitude}` —
  // `metadata.location` is a STRING in seed data and an OBJECT in real data, so reading
  // .latitude off it rendered the literal "undefined, undefined" on the certificate card.
  const humanAddress =
    (last_scan as any)?.metadata?.address ||
    getScanLocationLabel((last_scan as any)?.metadata);
  // VERA-415 / VERA-908: the last scan can now BE an invalid one (the API's last_scan
  // CTE includes `invalid_scan`), so the card says so instead of labelling a failed read
  // "Last scanned" like any other.
  const lastScanInvalid = isInvalidScan(last_scan as any);
  // No line at all when the location is withheld. "Scan location is hidden" spent a
  // whole row telling the reader something they can neither act on nor change.
  const title = humanAddress || null;

  // `formatActivityTime`, not `format(new Date(raw))`. Two reasons, both load-bearing:
  // recency is the information here ("6 days ago" beats "08/04/26, 19:50", which makes
  // the reader do the subtraction), and the raw value is a Postgres timestamp
  // (`2026-08-06 22:47:14.004772`) which **WebKit parses as Invalid Date**. The old call
  // then threw `RangeError: Invalid time value` out of `format()` and took the card
  // down with it — on iOS Safari, which is THE runtime for an app you reach by tapping
  // an NFC tag with a phone. `formatActivityTime` normalises via `parseApiDate` and
  // returns '' instead of throwing.
  const date = last_scan ? formatActivityTime((last_scan as any)?.created_at) : null;

  // VERA-304: a TRANSFER row must be dated from the transfer, not from `date`
  // above (the last SCAN). They are unrelated timestamps and diverge by however
  // long the item sat between its last scan and being gifted — measured live on
  // a Rolex gifted at 2026-08-20 08:46 whose card read "Jun 25, 21:17", its last
  // scan, i.e. the card claimed the gift happened ~8 weeks before it did.
  //
  // The incoming block already did this correctly; the three outgoing blocks did
  // not, so the same card family showed a real date to the recipient and a wrong
  // one to the sender.
  const transferDate = transfer?.created_at ? formatActivityTime(transfer.created_at as any) : null;

  /*
   * VRG-511 (list-row consistency) — ONE two-column row for every dated activity line.
   *
   * ## What was wrong
   *
   * The four dated blocks below put the timestamp in two different places:
   *
   *     "18 hours ago  Gift sent to @giftverify. Not claimed yet"   time FIRST
   *     "Last scanned  19 hours ago"                                 time LAST
   *
   * so scanning a list of eight cards meant hunting for the time in two positions.
   * Right-aligning it into a column of its own means the eye finds every timestamp in
   * one place, and it separates WHAT happened (left) from WHEN (right) — two different
   * questions that were sharing a line.
   *
   * ## ⚠️ The trap this is built to avoid, which already bit this codebase today
   *
   * Right-aligning a time onto a row of long variable text is what broke the gift-out
   * card: a 149px date squeezed the name column to 96px and ellipsised a plain
   * 16-character name. That was fixed by DROPPING the timestamp, and that escape is not
   * available here — the relative time IS the content of these rows.
   *
   * So the geometry is load-bearing, not decoration:
   *   - `minWidth: 0` on the message column. A flex item defaults to `min-width: auto`,
   *     sized to its longest unbreakable word, so WITHOUT this the column refuses to
   *     shrink and pushes the time off the row entirely.
   *   - `flexShrink: 0` + `nowrap` on the time. It is short and fixed-ish; the message
   *     is long and variable, so the message is the one that must yield. A wrapped
   *     "18 hours\nago" is the failure this prevents.
   *   - `alignItems: 'flex-start'` — TOP, not centre. When the message wraps to two
   *     lines a centred time floats against nothing and reads as broken.
   *
   * The column takes its NATURAL width rather than a fixed one. A fixed width would
   * have to fit the widest possible string — the prior-year absolute form,
   * "Aug 21 2025, 19:50" — and would then spend that width on every row, including the
   * "2 days ago" ones. Right-alignment already gives the consistent RIGHT edge the
   * request asked for; a consistent LEFT edge is not worth the widest case taxing every
   * row on a 375px phone.
   *
   * `lineHeight: 24px` matches the first line of every message block so the time sits
   * on the same baseline as the words it dates, rather than 2px above them.
   */
  /*
   * VRG-511 — an activity row is THREE parts, and which container each lives in is the
   * whole design.
   *
   *   [avatar]  Gift received                      14 hours ago   <- headline + time
   *             A gift from Anonymous. Claim it to add it to     <- description
   *             your certificates
   *
   * ## Why this is a shape and not a component
   *
   * The three parts do not share one parent, so they cannot be one node. `headline` and
   * `time` sit in a flex row BESIDE the avatar slot; `description` sits BELOW that
   * entire row, indented to the headline's left edge. A component returning a single
   * element could only ever put the description inside the text column, which is exactly
   * the bug this replaces — and note that the indent does NOT reintroduce it: the
   * description is a sibling with a margin, so its width is the card's, not whatever the
   * right-aligned time left over.
   *
   * ## The bug, measured
   *
   * The description used to be nested inside the headline's column, so it inherited
   * that column's width — and that column is what is left after the right-aligned time
   * takes its share. Measured at 375px: the description rendered 150px wide inside a
   * 309px card, 48% of the space available, so "A gift from Anonymous. Claim it to add
   * it to your certificates" wrapped onto three lines. It was being sized by its
   * SIBLING's text, which is why no `maxWidth` or `flex` on the paragraph could have
   * fixed it while it lived there.
   *
   * ## What each part may contain
   *
   *   headline     short and fixed-length. It shares row 1 with the time, so it must
   *                always fit — this is what stops the long text being squeezed.
   *   time         right-aligned, `nowrap`, never wraps.
   *   description  long and variable. Wraps freely, never truncates. Indented to the
   *                headline's left edge, so it runs `card - ACTIVITY_TEXT_INDENT_PX`.
   *
   * A state with no headline/description split (in_transfer, cancel_request…) passes
   * its whole sentence as `headline` and no time; it then owns row 1 alone.
   */
  type ActivityContent = {
    headline: React.ReactNode;
    time?: string | null;
    description?: React.ReactNode;
  };

  const scanBlock: ActivityContent = {
    headline: (
      <Typography variant='body2' lineHeight='24px' sx={{ fontWeight: 500 }}>
        Last scanned
      </Typography>
    ),
    time: date,
    // The location, when there is one. It is the long variable part of this row, so it
    // is the DESCRIPTION and gets the full card width.
    description: title ? (
      <Typography variant='body2' sx={theme => ({ color: theme.palette.text.secondary })}>
        {title}
      </Typography>
    ) : null,
  };

  /*
    THE EVENT EVERY CERTIFICATE HAS, for the rows where no scan exists.

    `hasActivityToShow` used to return false here and the whole strip collapsed,
    which was right when the alternative was printing "Last scanned" for a
    certificate with `scan_counter: 0` — an event that never happened. But
    collapsing reported our query's blind spot as the item's history: there WAS
    an event, the brand issued the thing. On a stretched grid row that showed up
    as a card with a hole in it next to a card with a strip.

    So the fix is a different TRUE sentence rather than a placeholder. "No recent
    activity" was the other candidate and is worse on both counts — it spends the
    same height to say nothing, and it is misleading by omission.

    ⚠️ WORDING IS PROVISIONAL. `created_at` is the certificate RECORD's creation,
    i.e. when the brand minted it, which can predate the owner receiving it. That
    makes "Issued" true but not necessarily the date the owner remembers, and for
    an item received by transfer it names a moment before they owned it. The
    alternatives ("Added", "Created") are each wrong in a different direction.
    Confirm against the activation-trigger semantics before this reaches
    customers.
  */
  const issuedBlock: ActivityContent | null = created_at
    ? {
        headline: (
          <Typography variant='body2' lineHeight='24px' sx={{ fontWeight: 500 }}>
            Issued
          </Typography>
        ),
        time: formatActivityTime(created_at as any),
        // No description: the brand's name is already the card's subject, and the
        // avatar beside this row is its mark. Repeating it would be the second
        // line this strip is trying not to need.
        description: null,
      }
    : null;

  /*
    ONE definition for what were three byte-identical copies (`out_transfer`,
    `transfer_initiated`, `transfer_waiting_confirmation`) — each carrying its own copy
    of the same three explanatory comments, which is exactly the arrangement that let
    the `transferDate` fix below revert in two of them while reading as fixed in the
    third. There is now one place to change, so "check the OTHER two blocks" no longer
    applies: there are no other blocks.

    `transferDate`, NOT `date`. `date` is the last SCAN, which on a gifted item is
    whenever it was last tapped, not when it was gifted. Measured: a Rolex gifted
    2026-08-21 07:45 whose card read "Jun 25, 21:17" — its last scan, i.e. the card
    dated the gift eight weeks before it happened. This reverted once already: the
    2026-08-21 VERA-315 fold resolved this file with `-X theirs`, keeping the
    `transferDate` DEFINITION and its comment while restoring the call sites to `date`,
    so the code read as fixed and rendered as broken.

    The date is no longer GUARDED here — the renderer omits the time element entirely
    for a falsy value, which is the same protection the old `{transferDate && …}` gave
    against the empty bold line that read as a broken gap.

    ⚠️ Two paragraphs that used to live here are DELETED rather than left standing,
    because both described the pre-VRG-511 layout and now argue for the opposite of
    what the code does: one said this block deliberately has NO heading, and one
    explained why the date and sentence had to share a single inline flow for
    `-webkit-line-clamp` to work. Both are answered inside the block below.
  */
  const outgoingTransferBlock: ActivityContent = {
    /*
      A LABEL on row 1, the recipient beneath — structurally identical to `__incoming`.

      This block used to lead with the date inline and carry no heading, on the argument
      that a card already showing a `Transfer` status, whose next line read "Transferred
      to <name>", would print the same word a THIRD time. Sound for the layout it was
      written for; it does not survive the timestamp moving to a right-aligned column.

      Without a label the whole sentence shares row 1 with the time, and the widest real
      string — "Gift sent to @spencergiftnew2. Not claimed yet" — was ellipsised to
      "…Not…". The label IS the fix: it is short and fixed-length, so IT shares row 1 and
      always fits, while the recipient's name drops to the full-width description where
      it can wrap. The residual repetition ("Transferred" beside a `Transfer` pill) is
      accepted knowingly — a truncated name is worse than a repeated verb, and for the
      commoner gift case the words differ anyway.

      No `-webkit-line-clamp`. It bounded card height when date and sentence shared one
      inline flow, and it is also what turned overflow into an ellipsis. At full width
      this wraps to two lines cleanly, exactly as `__incoming` always has.
    */
    headline: (
      <Typography variant='body2' lineHeight='24px' sx={{ fontWeight: 500 }}>
        {isGift ? 'Gift sent' : 'Transferred'}
      </Typography>
    ),
    time: transferDate,
    description: (
      <Typography variant='body2' sx={theme => ({ color: theme.palette.text.secondary })}>
        to <span style={{ fontWeight: 700 }}>{buyer}.</span> Not claimed yet
      </Typography>
    ),
  };

  const notificationText = () => {
    /*
      Every entry is an `ActivityContent`, including the ones with nothing but a
      sentence: those pass it as `headline` and own row 1 alone. Normalising here rather
      than allowing a bare node means the renderer has exactly one shape to handle.
    */
    const messages: { [key: string]: ActivityContent | null } = {
      // VRG-720: the scan line when there IS a scan, the issue line otherwise.
      active: last_scan ? scanBlock : issuedBlock,
      inactive: last_scan ? scanBlock : issuedBlock,
      // Branch on whether a scan EXISTS, not on the `scanned` prop. The list card
      // hardcodes `scanned` to true, so the report-it prompt below — the one piece of
      // advice this app can offer someone whose item was stolen — could never render
      // from the list. A stolen certificate with no sighting is exactly the case that
      // needs it.
      stolen: last_scan
        ? scanBlock
        : {
            headline: (
              <Typography variant='body2' lineHeight='24px'>
                Share your certificate and scanning history with the police
              </Typography>
            ),
          },
      in_transfer: {
        headline: (
          <Typography variant='body2' lineHeight='20px'>
            <span style={{ fontWeight: 700 }}>{seller}</span>
            {isGift
              ? ' sent you this gift. Accept it to add it to your certificates'
              : ' has transferred this item to you. Accept transfer to add it to your certificates'}
          </Typography>
        ),
      },
      // VERA-304: the recipient's mirror of `out_transfer`. Same three-line shape so a
      // list containing both directions reads as one card family, with the roles and
      // the verb reversed. Both directions are dated from the TRANSFER via
      // `transferDate` — see its definition for why `date` (the last scan) is wrong
      // here.
      __incoming: {
        /*
          VERA-304: the heading names the EVENT, and a gift is not a transfer. The two
          words match `CARD_HEADING` on the certificate page's pending-transfer card
          (`transferSummary/tranferSummary.tsx`) so one item is called the same thing in
          the list and on its own page — the incoming-PRIMARY heading deliberately does
          not ("Transfer in" here, "Transfer received" there), which is why this is a
          local literal rather than an import of that table.
        */
        headline: (
          <Typography variant='body2' lineHeight='24px' sx={{ fontWeight: 500 }}>
            {isGift ? 'Gift received' : 'Transfer in'}
          </Typography>
        ),
        time: transferDate,
        description: (
          <Typography variant='body2' sx={theme => ({ color: theme.palette.text.secondary })}>
            {isGift ? 'A gift from ' : 'From '}
            <span style={{ fontWeight: 700 }}>{seller}.</span> Claim it to add it to your certificates
          </Typography>
        ),
      } as ActivityContent,
      out_transfer: outgoingTransferBlock,
      waiting_accept: {
        headline: (
          <Typography variant='body2'>
            <span style={{ fontWeight: 700 }}>{seller}</span>
            {isGift
              ? ' sent you this gift. Accept it to add it to your certificates'
              : ' has transferred this item to you. Accept transfer to add it to your certificates'}
          </Typography>
        ),
      },
      transfer_initiated: outgoingTransferBlock,
      transfer_waiting_confirmation: outgoingTransferBlock,
      lost: last_scan ? scanBlock : issuedBlock,
      cancel_request: {
        headline: (
          <Typography variant='body2'>
            <span style={{ fontWeight: 700 }}>{seller}</span>
            {isGift
              ? ' requests to cancel this gift.'
              : ' requests to cancel the transfer of this item to you.'}
          </Typography>
        ),
      },
      returned: {
        headline: <Typography variant='body2'>You have returned this item</Typography>,
      },
    };

    // Same override as the icon: an invalid read is reported as such rather than being
    // dressed up as an ordinary "Last scanned" line.
    if (lastScanInvalid) {
      return {
        headline: (
          <Typography variant='body2' lineHeight='24px' sx={{ fontWeight: 700 }}>
            Invalid scan
          </Typography>
        ),
        time: date,
        description: title ? (
          <Typography variant='body2' sx={theme => ({ color: theme.palette.text.secondary })}>
            {title}
          </Typography>
        ) : null,
      } as ActivityContent;
    }

    // VERA-304: the two "a transfer is in flight" statuses are the same certificate
    // state seen from opposite ends, so the viewer decides which sentence they get.
    // Everything else in this map is direction-neutral and falls through unchanged.
    if (
      isIncoming &&
      (status === 'transfer_initiated' || status === 'transfer_waiting_confirmation')
    ) {
      return (messages as any).__incoming;
    }

    return messages[status] || null;
  };

  const notificationIcon = () => {
    // An invalid last scan replaces the scan glyph regardless of certificate status —
    // the status axis (Active/Lost/...) and the scan-outcome axis are independent, and
    // the failed read is the more urgent of the two.
    if (lastScanInvalid) {
      return <ShieldAlert size={ACTIVITY_GLYPH_FILL_PX} strokeWidth={1.25} color={palette.error.main} />;
    }
    switch (status) {
      case 'active':
      case 'inactive':
      case 'lost':
        /*
          The scan mark ONLY when the row is about a scan. With no scan the row
          says "Issued", and a scan glyph beside it would reassert the very event
          `hasActivityToShow` was written to stop the card claiming — the brand's
          mark is both true and more use, and it matches the `default` row.
        */
        return last_scan ? (
          <svg width={ACTIVITY_GLYPH_FILL_PX} height={ACTIVITY_GLYPH_FILL_PX}>
            <use href='#scan' />
          </svg>
        ) : (
          <EntityAvatar
            entity='company'
            src={company?.logo}
            name={company?.name}
            size={ACTIVITY_GLYPH_FILL_PX}
          />
        );
      case 'stolen':
        // Same condition as the stolen COPY above, so glyph and text always agree:
        // the scan mark when there is a sighting to report, the info mark when the
        // row is the "report it to the police" prompt instead.
        return last_scan ? (
          <svg width={ACTIVITY_GLYPH_FILL_PX} height={ACTIVITY_GLYPH_FILL_PX}>
            <use href='#scan' />
          </svg>
        ) : (
          <svg width={ACTIVITY_GLYPH_FILL_PX} height={ACTIVITY_GLYPH_FILL_PX}>
            <use href='#info' />
          </svg>
        );
      case 'transfer_initiated':
      case 'transfer_waiting_confirmation':
      case 'out_transfer':
        // VERA-304: the counterparty's face, not a verb glyph. `UserRoundMinus` drew
        // the same anonymous outline on every transfer row, so a list of them was
        // indistinguishable until you read the sentences. These three statuses are the
        // only ones that can be seen from EITHER end, which is why they resolve the
        // party through `counterparty` rather than falling through to the seller-only
        // `default` below. Same fill inside the same slot as every other avatar row.
        return (
          <EntityAvatar
            entity={counterparty.entity}
            src={counterparty.src}
            name={counterparty.name}
            size={ACTIVITY_GLYPH_FILL_PX}
          />
        );
      default:
        return (
          <EntityAvatar
            entity={sellerIsCompany ? 'company' : 'person'}
            src={image}
            name={seller}
            size={ACTIVITY_GLYPH_FILL_PX}
          />
        );
    }
  };

  // Nothing truthful to report — render nothing rather than an empty heading.
  if (!hasActivityToShow({ status, last_scan, created_at })) return null;

  const content = notificationText();
  if (!content) return null;

  return (
    /*
      CENTER, not `start`.

      The icon well is a fixed `ACTIVITY_GLYPH_SLOT_PX` and the copy beside it is
      one, two or three lines depending on the state, so top-aligning them made the
      text ride high against the well by however much the well overhung it — most
      visibly on the one-line rows ("Last scanned <date>"), where 20px of text sat
      against a slot half again its height and looked pasted to the top. Centring makes the pairing read the same
      at every length, which is the only way a list of mixed-length rows looks
      deliberate.
    */
    /*
      ONE row: [avatar][ text column ]. The text column holds the headline+time row and,
      beneath it, the description — so the avatar spans both lines.

      Three shapes were tried and the differences are subtle enough to be worth writing
      down, because each fixed the previous one's bug and introduced its own:

        1. description nested INSIDE the headline's row  → inherited a width the
           right-aligned time had already claimed: 150px of a 309px card, wrapping a
           two-line sentence to three.
        2. description a sibling of the whole avatar row  → full width, correct, but it
           began below the avatar's 48px while the headline text ends at ~20px. The 12px
           of empty avatar column read as a gap that no spacing change could shrink,
           because it was not spacing (the boxes measured flush, margin 0, gap normal).
        3. description a sibling of the headline ROW, inside the text COLUMN  → keeps
           (2)'s width, and the avatar now spans both lines so the only vertical distance
           is real spacing. Also makes the indent structural, so the explicit `ml` is gone.

      `alignItems: center` centres the avatar against the text BLOCK, one line or two.
    */
    <Stack
      direction={'row'}
      alignItems={'center'}
      sx={{ gap: `${ACTIVITY_ROW_GAP_PX}px`, paddingRight: '8px' }}
    >
      {/*
        VERA-304: the SLOT is `ACTIVITY_GLYPH_SLOT_PX` on every row; the FILL is only
        drawn when it carries information.

        A neutral grey well was drawn behind whatever went in this slot, which meant a
        frame around a frame for the two cases that already bound themselves: an
        `EntityAvatar` person is a 40px grey circle, so it rendered as a circle inside a
        square; an `EntityAvatar` company is a white 40px logo tile, so the brand mark
        (reported on Louis Vuitton) appeared to sit in grey padding — the tile's own
        white plate ringed by 4px of `grey[50]`. Neither needed a container, and around
        the bare 24px glyph rows ("Last scanned") the box was decoration rather than a
        boundary.

        The red tint STAYS, because it is the one case where the fill says something the
        contents do not repeat — an invalid read — the same "tint carries state, chrome
        does not" split §32 makes for destructive triggers.

        The footprint is kept regardless so every row aligns on one left edge.

        The GLYPH rows draw at the SAME fill as the avatars rather than smaller. In a
        list that alternates the two, a mark drawn well inside the slot read as a
        smaller, lesser thing than the neighbouring faces — the rows are peers and the
        alignment was already promising that. `strokeWidth` stays 1.25: it was dropped
        from 1.75 when the fill grew, and it is the right weight at this size too.

        VRG-720 took slot and fill down together, 48/40 -> 32/28, because the strip was
        taller than the title block it sits beneath (see `ACTIVITY_STRIP_MIN_H`). The
        peer relationship above is preserved — it is a ratio, not a pair of numbers.
      */}
      <Stack
        className='min-w-8 w-8 h-8 rounded-md text-center items-center justify-center'
        sx={{
          backgroundColor: lastScanInvalid ? alpha(palette.error.main, 0.08) : 'transparent',
          // `border: 'none'`, NOT a transparent border. `index.css`'s
          // `#vera-shadow-root *` sets `border-color` at ID specificity, so an authored
          // `1px solid transparent` still painted the forced `#E4E4E7` — measured in the
          // browser after the first attempt at this. Only zero WIDTH is safe here; it is
          // the same trap `stateCardSurface.ts` and `EntityAvatar` both record.
          border: lastScanInvalid ? `1px solid ${alpha(palette.error.main, 0.24)}` : 'none',
        }}
      >
        {notificationIcon()}
      </Stack>
      {/*
        Row 1's text column. `flexGrow: 1` + `minWidth: 0` are both load-bearing: this is
        a flex ITEM, which defaults to shrink-wrapping its content, so without them each
        row sized to its own headline and the right-aligned times landed at different x
        down the card stack — measured 241px vs 169.5px, i.e. times 72px apart. Every row
        individually looked right-aligned and the COLUMN did not exist.
      */}
      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
        <Stack direction='row' alignItems='flex-start' spacing={1.5}>
          <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{content.headline}</Box>
          {content.time ? (
            <Typography
              variant='body2'
              sx={theme => ({
                flexShrink: 0,
                whiteSpace: 'nowrap',
                textAlign: 'right',
                lineHeight: '24px',
                color: theme.palette.text.secondary,
              })}
            >
              {content.time}
            </Typography>
          ) : null}
        </Stack>
        {/*
          Row 2 — a CHILD of the text column, a SIBLING of the headline+time row.

          Both halves of that matter and they are easy to conflate:

          - Sibling of the headline ROW (not a third item inside it). As a flex item in
            that row it inherited a width the right-aligned time had already taken its
            share of — 150px of a 309px card — and wrapped to three lines. No `maxWidth`
            or `flex` on the paragraph could have fixed it there.
          - Child of the text COLUMN (not of the outer avatar row, where it lived until
            VRG-514's second pass). That earlier position aligned it correctly via an
            `ml` equal to the glyph slot, and looked right, but it sat BELOW the avatar
            rather than beside it — so it started after the avatar's full height while
            the headline text ends at ~20px. The empty avatar column between them read
            as a gap and could not be tuned away, because it was not spacing: no margin,
            no `gap`, the two boxes measured flush. Nesting here makes the avatar span
            BOTH lines, so the only distance between them is real spacing.

          The indent is now structural (the column starts past the avatar), so the `ml`
          is gone. `ACTIVITY_TEXT_INDENT_PX` survives for the width arithmetic in the
          docblock above.
        */}
        {/*
          ONE LINE, ellipsised — the strip's height invariant depends on it.

          A description that wraps to TWO lines adds a whole line to the strip, so the
          clamp is what keeps the two-line case from becoming a three-line one. Measured
          at 1024 before the clamp: "to @sjfstebbins. Not claimed yet" wrapped, and that
          card stood 17px above every "Last scanned" neighbour.

          ⚠️ The strip is NO LONGER one height for every row, and that changed with
          VRG-720 rather than with this clamp. At 48/16 the slot (48) beat the tallest
          text block (a 24px headline over a 20px description = 44), so the well won
          everywhere and every strip measured 80. At 32/12 the slot is 32 and that same
          block is still 44, so it now wins instead: a one-line row is 56 and a
          headline+description row is 68.

          That 12px spread is the price of the strip no longer being taller than the
          title block above it, and it is paid where it shows least — the cards stretch
          to a common height in the grid and the strip is bottom-aligned, so the only
          visible effect is two dividers in one row sitting 12px apart when one card
          carries a description and its neighbour does not. Worth it; stated so the next
          person does not read the old invariant into the new numbers.

          Clamped HERE, in the renderer, rather than inside the transfer block that
          happened to overflow first: the long variable content is the description slot
          by design (see the ActivityContent docblock), so any future block inherits the
          bound instead of re-discovering it. The `headline` is deliberately NOT clamped
          — the states with no headline/description split pass a whole sentence as
          headline and own row 1 alone, where two lines still fit inside the 48px well.

          NOT `-webkit-line-clamp`. This Box is a flex item, and a flex item's
          `display: -webkit-box` is blockified to `flow-root`, which silently disables
          the clamp — the trap CertificatesTeaser's title records in full after it ate
          the product name. For a single line `nowrap` + `textOverflow` needs no
          `display` hack at all, so there is nothing to blockify.

          `minWidth: 0` on both boxes is what lets the truncation happen: a flex item
          defaults to `min-width: auto`, sized to its longest unbreakable word, so
          without it the Box refuses to shrink and the text overflows the card instead
          of ellipsising. The child selector carries the properties onto the Typography
          because `text-overflow` acts on a box's own INLINE content, and from the Box's
          side the Typography is a BLOCK child — set on the Box alone it would do
          nothing.
        */}
        {content.description ? (
          <Box
            sx={{
              minWidth: 0,
              '& > *': {
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          >
            {content.description}
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
};
